import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Server,
  Plus,
  Search,
  Filter,
  AlertTriangle,
  Shield,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Trash2,
  Edit,
  X,
  Bell,
  BellRing,
  Activity,
  Database,
  Cloud,
  Cpu,
  Network,
  Smartphone,
  AlertCircle,
  Eye,
  Check,
  Target,
  TrendingUp,
  ArrowUpDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssetInventory = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Assets state
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetForm, setAssetForm] = useState({
    name: '',
    vendor: '',
    product: '',
    version: '',
    category: 'software',
    criticality: 'medium',
    environment: 'production',
    owner: '',
    department: '',
    notes: ''
  });
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertStats, setAlertStats] = useState({});
  const [alertFilter, setAlertFilter] = useState('open');
  const [alertSortBy, setAlertSortBy] = useState('priority');
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  
  // Scan state
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState('');

  useEffect(() => {
    fetchDashboard();
    fetchAssets();
    fetchAlerts();
  }, []);

  // Refetch alerts when filter or sort changes
  useEffect(() => {
    fetchAlerts();
  }, [alertFilter, alertSortBy]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchDashboard = async () => {
    setDashboardLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/asset-inventory/dashboard`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchAssets = async () => {
    setAssetsLoading(true);
    try {
      let url = `${API_URL}/api/asset-inventory`;
      const params = [];
      if (categoryFilter) params.push(`category=${categoryFilter}`);
      if (criticalityFilter) params.push(`criticality=${criticalityFilter}`);
      if (params.length) url += `?${params.join('&')}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data = await response.json();
      setAssets(data.assets || []);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setAssetsLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      let url = `${API_URL}/api/asset-inventory/alerts?sort_by=${alertSortBy}`;
      if (alertFilter && alertFilter !== 'all') url += `&status=${alertFilter}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data = await response.json();
      setAlerts(data.alerts || []);
      setAlertStats(data.stats || {});
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      const url = editingAsset 
        ? `${API_URL}/api/asset-inventory/${editingAsset.id}`
        : `${API_URL}/api/asset-inventory`;
      
      const response = await fetch(url, {
        method: editingAsset ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(assetForm)
      });
      
      if (response.ok) {
        setShowAssetModal(false);
        setEditingAsset(null);
        setAssetForm({
          name: '', vendor: '', product: '', version: '',
          category: 'software', criticality: 'medium', environment: 'production',
          owner: '', department: '', notes: ''
        });
        fetchAssets();
        fetchDashboard();
        setTimeout(() => fetchAlerts(), 2000);
      }
    } catch (error) {
      console.error('Failed to save asset:', error);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset? Associated alerts will also be removed.')) return;
    
    try {
      await fetch(`${API_URL}/api/asset-inventory/${assetId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchAssets();
      fetchDashboard();
      fetchAlerts();
    } catch (error) {
      console.error('Failed to delete asset:', error);
    }
  };

  const handleUpdateAlertStatus = async (alertId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/asset-inventory/alerts/${alertId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      fetchAlerts();
      fetchDashboard();
    } catch (error) {
      console.error('Failed to update alert:', error);
    }
  };

  const handleTriggerScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const response = await fetch(`${API_URL}/api/asset-inventory/scan`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setScanResult(data);
      
      setTimeout(() => {
        fetchAlerts();
        fetchDashboard();
      }, 3000);
    } catch (error) {
      console.error('Failed to trigger scan:', error);
    } finally {
      setScanning(false);
    }
  };

  const openEditModal = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name,
      vendor: asset.vendor,
      product: asset.product,
      version: asset.version || '',
      category: asset.category,
      criticality: asset.criticality,
      environment: asset.environment,
      owner: asset.owner || '',
      department: asset.department || '',
      notes: asset.notes || ''
    });
    setShowAssetModal(true);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'software': Database,
      'hardware': Cpu,
      'cloud': Cloud,
      'network': Network,
      'iot': Smartphone
    };
    return icons[category] || Server;
  };

  const getCriticalityColor = (criticality) => {
    const colors = {
      'critical': 'bg-red-500/20 text-red-400 border-red-500/30',
      'high': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'low': 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[criticality] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-red-500/20 text-red-400',
      'acknowledged': 'bg-yellow-500/20 text-yellow-400',
      'in_progress': 'bg-blue-500/20 text-blue-400',
      'resolved': 'bg-green-500/20 text-green-400',
      'false_positive': 'bg-slate-500/20 text-slate-400'
    };
    return colors[status] || colors.open;
  };

  const filteredAssets = assets.filter(a => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      a.name?.toLowerCase().includes(search) ||
      a.vendor?.toLowerCase().includes(search) ||
      a.product?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Server className="w-8 h-8 text-violet-400" />
              Asset Inventory & Vulnerability Alerts
            </h1>
            <p className="text-slate-400 mt-2">
              Manage your assets and receive automated alerts when vulnerabilities are found in CISA KEV
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleTriggerScan}
              disabled={scanning}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 text-white rounded-lg transition-colors"
            >
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
              Scan for Vulnerabilities
            </button>
            <button
              onClick={() => { setEditingAsset(null); setAssetForm({ name: '', vendor: '', product: '', version: '', category: 'software', criticality: 'medium', environment: 'production', owner: '', department: '', notes: '' }); setShowAssetModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Asset
            </button>
          </div>
        </div>

        {/* Scan Result Banner */}
        {scanResult && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-green-300">{scanResult.message}</span>
              {scanResult.assetsToScan && (
                <span className="text-slate-400">({scanResult.assetsToScan} assets scanned)</span>
              )}
            </div>
            <button onClick={() => setScanResult(null)} className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'dashboard' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Activity className="w-4 h-4" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('assets')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'assets' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Server className="w-4 h-4" />
            Assets ({assets.length})
          </button>
          <button
            onClick={() => { setActiveTab('alerts'); fetchAlerts(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'alerts' ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <BellRing className="w-4 h-4" />
            Alerts
            {alertStats.open > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{alertStats.open}</span>
            )}
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {dashboardLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
              </div>
            ) : dashboard ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 text-center">
                    <div className="text-3xl font-bold text-white">{dashboard.assets?.total || 0}</div>
                    <div className="text-sm text-slate-400">Total Assets</div>
                  </div>
                  <div className="bg-red-500/10 rounded-xl border border-red-500/30 p-4 text-center">
                    <div className="text-3xl font-bold text-red-400">{dashboard.alerts?.open || 0}</div>
                    <div className="text-sm text-slate-400">Open Alerts</div>
                  </div>
                  <div className="bg-orange-500/10 rounded-xl border border-orange-500/30 p-4 text-center">
                    <div className="text-3xl font-bold text-orange-400">{dashboard.alerts?.overdue || 0}</div>
                    <div className="text-sm text-slate-400">Overdue</div>
                  </div>
                  <div className="bg-amber-500/10 rounded-xl border border-amber-500/30 p-4 text-center">
                    <div className="text-3xl font-bold text-amber-400">{dashboard.alerts?.dueSoon || 0}</div>
                    <div className="text-sm text-slate-400">Due Soon (7 days)</div>
                  </div>
                </div>

                {/* Priority Distribution */}
                {dashboard.alerts?.priorityDistribution && (
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-violet-400" />
                      Priority Distribution (Open Alerts)
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg text-center bg-red-500/20 border border-red-500/30">
                        <div className="text-2xl font-bold text-red-400">{dashboard.alerts.priorityDistribution.P1 || 0}</div>
                        <div className="text-sm text-red-300 font-medium">P1 Critical</div>
                        <div className="text-xs text-slate-400 mt-1">Remediate Now</div>
                      </div>
                      <div className="p-4 rounded-lg text-center bg-orange-500/20 border border-orange-500/30">
                        <div className="text-2xl font-bold text-orange-400">{dashboard.alerts.priorityDistribution.P2 || 0}</div>
                        <div className="text-sm text-orange-300 font-medium">P2 High</div>
                        <div className="text-xs text-slate-400 mt-1">This Week</div>
                      </div>
                      <div className="p-4 rounded-lg text-center bg-yellow-500/20 border border-yellow-500/30">
                        <div className="text-2xl font-bold text-yellow-400">{dashboard.alerts.priorityDistribution.P3 || 0}</div>
                        <div className="text-sm text-yellow-300 font-medium">P3 Medium</div>
                        <div className="text-xs text-slate-400 mt-1">This Month</div>
                      </div>
                      <div className="p-4 rounded-lg text-center bg-green-500/20 border border-green-500/30">
                        <div className="text-2xl font-bold text-green-400">{dashboard.alerts.priorityDistribution.P4 || 0}</div>
                        <div className="text-sm text-green-300 font-medium">P4 Low</div>
                        <div className="text-xs text-slate-400 mt-1">When Possible</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Top Priority Alerts */}
                {dashboard.topPriorityAlerts?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      Top Priority Alerts - Remediate First
                    </h3>
                    <div className="space-y-3">
                      {dashboard.topPriorityAlerts.map((alert, idx) => (
                        <div key={idx} className="p-4 bg-slate-900/50 rounded-lg border-l-4" style={{
                          borderLeftColor: alert.priority?.gradeColor === 'red' ? '#ef4444' : 
                                          alert.priority?.gradeColor === 'orange' ? '#f97316' :
                                          alert.priority?.gradeColor === 'yellow' ? '#eab308' : '#22c55e'
                        }}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                alert.priority?.grade === 'P1' ? 'bg-red-500 text-white' :
                                alert.priority?.grade === 'P2' ? 'bg-orange-500 text-white' :
                                alert.priority?.grade === 'P3' ? 'bg-yellow-500 text-black' :
                                'bg-green-500 text-white'
                              }`}>
                                {alert.priority?.grade} • Score: {alert.priority?.score}
                              </span>
                              <a href={`https://nvd.nist.gov/vuln/detail/${alert.cveId}`} target="_blank" rel="noopener noreferrer" className="font-mono text-red-400 hover:text-red-300 text-sm">{alert.cveId}</a>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs ${getCriticalityColor(alert.assetCriticality)}`}>{alert.assetCriticality} asset</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{alert.assetName}</p>
                              <p className="text-slate-400 text-sm truncate">{alert.vulnerabilityName}</p>
                            </div>
                            <div className="text-right">
                              <p className={`text-sm font-medium ${alert.priority?.breakdown?.urgency?.label === 'OVERDUE' ? 'text-red-400' : 'text-slate-300'}`}>
                                {alert.priority?.breakdown?.urgency?.label}
                              </p>
                              <p className="text-xs text-slate-500">Due: {alert.dueDate}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      Top Affected Vendors in Your Inventory
                    </h3>
                    {dashboard.topAffectedVendors?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.topAffectedVendors.map((v, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                            <span className="text-white font-medium">{v.vendor}</span>
                            <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">{v.count} alerts</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <Shield className="w-12 h-12 mx-auto mb-3 text-green-500" />
                        <p>No vulnerability alerts found!</p>
                        <p className="text-sm mt-1">Add assets and run a scan to check for vulnerabilities.</p>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <BellRing className="w-5 h-5 text-amber-400" />
                      Recent Alerts
                    </h3>
                    {dashboard.recentAlerts?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.recentAlerts.map((alert, idx) => (
                          <div key={idx} className="p-3 bg-slate-900/50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <a href={`https://nvd.nist.gov/vuln/detail/${alert.cveId}`} target="_blank" rel="noopener noreferrer" className="font-mono text-red-400 hover:text-red-300 text-sm">{alert.cveId}</a>
                              <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(alert.status)}`}>{alert.status}</span>
                            </div>
                            <p className="text-white text-sm">{alert.assetName}</p>
                            <p className="text-slate-400 text-xs truncate">{alert.vulnerabilityName}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <Bell className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                        <p>No alerts yet</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                  <h3 className="font-semibold text-white mb-4">Assets by Criticality</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {['critical', 'high', 'medium', 'low'].map(level => (
                      <div key={level} className={`p-4 rounded-lg text-center ${getCriticalityColor(level)}`}>
                        <div className="text-2xl font-bold">{dashboard.assets?.byCriticality?.[level] || 0}</div>
                        <div className="text-sm capitalize">{level}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Assets Tab */}
        {activeTab === 'assets' && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search assets..." className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white" />
              </div>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                <option value="">All Categories</option>
                <option value="software">Software</option>
                <option value="hardware">Hardware</option>
                <option value="cloud">Cloud</option>
                <option value="network">Network</option>
                <option value="iot">IoT</option>
              </select>
              <button onClick={fetchAssets} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"><RefreshCw className="w-4 h-4" /></button>
            </div>

            {assetsLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-violet-500 animate-spin" /></div>
            ) : filteredAssets.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
                <Server className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Assets Found</h3>
                <p className="text-slate-400 mb-6">Add your first asset to start tracking vulnerabilities</p>
                <button onClick={() => setShowAssetModal(true)} className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg"><Plus className="w-5 h-5" />Add Asset</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAssets.map((asset) => {
                  const CategoryIcon = getCategoryIcon(asset.category);
                  return (
                    <div key={asset.id} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 hover:border-violet-500/50 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-violet-500/20 rounded-lg"><CategoryIcon className="w-5 h-5 text-violet-400" /></div>
                          <div>
                            <h4 className="font-semibold text-white">{asset.name}</h4>
                            <p className="text-sm text-slate-400">{asset.vendor} - {asset.product}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs border ${getCriticalityColor(asset.criticality)}`}>{asset.criticality}</span>
                      </div>
                      <div className="space-y-1 text-sm text-slate-400">
                        {asset.version && <p>Version: {asset.version}</p>}
                        <p>Environment: {asset.environment}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-700">
                        <button onClick={() => openEditModal(asset)} className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"><Edit className="w-3 h-3" />Edit</button>
                        <button onClick={() => handleDeleteAsset(asset.id)} className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="space-y-6">
            {/* Priority Distribution Summary */}
            {alertStats.priorityDistribution && (
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-red-400">{alertStats.priorityDistribution.P1 || 0}</div>
                  <div className="text-xs text-slate-400">P1 Critical</div>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-orange-400">{alertStats.priorityDistribution.P2 || 0}</div>
                  <div className="text-xs text-slate-400">P2 High</div>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-yellow-400">{alertStats.priorityDistribution.P3 || 0}</div>
                  <div className="text-xs text-slate-400">P3 Medium</div>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-green-400">{alertStats.priorityDistribution.P4 || 0}</div>
                  <div className="text-xs text-slate-400">P4 Low</div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-2 flex-wrap">
                {[{value:'all',label:'All',count:alerts.length},{value:'open',label:'Open',count:alertStats.open},{value:'acknowledged',label:'Acknowledged',count:alertStats.acknowledged},{value:'in_progress',label:'In Progress',count:alertStats.inProgress},{value:'resolved',label:'Resolved',count:alertStats.resolved}].map(f => (
                  <button key={f.value} onClick={() => setAlertFilter(f.value)} className={`px-4 py-2 rounded-lg text-sm ${alertFilter===f.value?'bg-red-600 text-white':'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>{f.label}{f.count>0&&<span className="ml-2 px-1.5 py-0.5 bg-black/20 rounded">{f.count}</span>}</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-slate-400" />
                <select 
                  value={alertSortBy} 
                  onChange={(e) => { setAlertSortBy(e.target.value); }}
                  className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                >
                  <option value="priority">Sort by Priority Score</option>
                  <option value="dueDate">Sort by Due Date</option>
                  <option value="createdAt">Sort by Date Added</option>
                </select>
              </div>
            </div>

            {alertsLoading ? (<div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>
            ) : alerts.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
                <Shield className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Alerts</h3>
                <p className="text-slate-400">{alertFilter==='all'?'No vulnerability alerts have been found.':'No '+alertFilter.replace('_',' ')+' alerts.'}</p>
              </div>
            ) : (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-900/50"><tr className="text-left text-sm text-slate-400"><th className="p-4">Priority</th><th className="p-4">CVE</th><th className="p-4">Asset</th><th className="p-4">Vulnerability</th><th className="p-4">Due Date</th><th className="p-4">Status</th><th className="p-4">Actions</th></tr></thead>
                  <tbody>
                    {alerts.map((alert) => {
                      const isOverdue = alert.dueDate < new Date().toISOString().split('T')[0];
                      const priority = alert.priority;
                      return (
                        <tr key={alert.id} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4">
                            {priority && (
                              <div className="flex flex-col items-center gap-1">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  priority.grade === 'P1' ? 'bg-red-500 text-white' :
                                  priority.grade === 'P2' ? 'bg-orange-500 text-white' :
                                  priority.grade === 'P3' ? 'bg-yellow-500 text-black' :
                                  'bg-green-500 text-white'
                                }`}>
                                  {priority.grade}
                                </span>
                                <span className="text-xs text-slate-500">{priority.score}</span>
                              </div>
                            )}
                          </td>
                          <td className="p-4"><a href={`https://nvd.nist.gov/vuln/detail/${alert.cveId}`} target="_blank" rel="noopener noreferrer" className="font-mono text-red-400 hover:text-red-300">{alert.cveId}</a></td>
                          <td className="p-4">
                            <div className="text-white text-sm">{alert.assetName}</div>
                            <div className="text-slate-400 text-xs">{alert.vendor} - {alert.product}</div>
                            {alert.assetCriticality && (
                              <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-xs ${getCriticalityColor(alert.assetCriticality)}`}>{alert.assetCriticality}</span>
                            )}
                          </td>
                          <td className="p-4 max-w-xs"><p className="text-slate-300 text-sm truncate">{alert.vulnerabilityName}</p></td>
                          <td className="p-4"><span className={`text-sm ${isOverdue?'text-red-400 font-semibold':'text-slate-400'}`}>{alert.dueDate}{isOverdue&&' ⚠️'}</span></td>
                          <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${getStatusColor(alert.status)}`}>{alert.status.replace('_',' ')}</span></td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              {alert.status==='open'&&<button onClick={()=>handleUpdateAlertStatus(alert.id,'acknowledged')} className="p-1.5 text-slate-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded" title="Acknowledge"><Eye className="w-4 h-4"/></button>}
                              {(alert.status==='open'||alert.status==='acknowledged')&&<button onClick={()=>handleUpdateAlertStatus(alert.id,'in_progress')} className="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded" title="Mark In Progress"><Activity className="w-4 h-4"/></button>}
                              {alert.status!=='resolved'&&<button onClick={()=>handleUpdateAlertStatus(alert.id,'resolved')} className="p-1.5 text-slate-400 hover:text-green-400 hover:bg-green-500/10 rounded" title="Resolve"><Check className="w-4 h-4"/></button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Asset Modal */}
        {showAssetModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">{editingAsset?'Edit Asset':'Add New Asset'}</h2>
                <button onClick={()=>{setShowAssetModal(false);setEditingAsset(null);}} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
              </div>
              <form onSubmit={handleCreateAsset} className="space-y-4">
                <div><label className="block text-sm text-slate-400 mb-1">Asset Name *</label><input type="text" value={assetForm.name} onChange={(e)=>setAssetForm({...assetForm,name:e.target.value})} required placeholder="e.g., Production Web Server" className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"/></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm text-slate-400 mb-1">Vendor *</label><input type="text" value={assetForm.vendor} onChange={(e)=>setAssetForm({...assetForm,vendor:e.target.value})} required placeholder="e.g., Microsoft, Apache" className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"/></div>
                  <div><label className="block text-sm text-slate-400 mb-1">Product *</label><input type="text" value={assetForm.product} onChange={(e)=>setAssetForm({...assetForm,product:e.target.value})} required placeholder="e.g., Windows Server" className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"/></div>
                </div>
                <div><label className="block text-sm text-slate-400 mb-1">Version</label><input type="text" value={assetForm.version} onChange={(e)=>setAssetForm({...assetForm,version:e.target.value})} placeholder="e.g., 2019" className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"/></div>
                <div className="grid grid-cols-3 gap-4">
                  <div><label className="block text-sm text-slate-400 mb-1">Category</label><select value={assetForm.category} onChange={(e)=>setAssetForm({...assetForm,category:e.target.value})} className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"><option value="software">Software</option><option value="hardware">Hardware</option><option value="cloud">Cloud</option><option value="network">Network</option><option value="iot">IoT</option></select></div>
                  <div><label className="block text-sm text-slate-400 mb-1">Criticality</label><select value={assetForm.criticality} onChange={(e)=>setAssetForm({...assetForm,criticality:e.target.value})} className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div>
                  <div><label className="block text-sm text-slate-400 mb-1">Environment</label><select value={assetForm.environment} onChange={(e)=>setAssetForm({...assetForm,environment:e.target.value})} className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"><option value="production">Production</option><option value="staging">Staging</option><option value="development">Development</option><option value="test">Test</option></select></div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-300"><AlertTriangle className="w-4 h-4 inline mr-2"/>After saving, the system will automatically check CISA KEV for matching vulnerabilities.</div>
                <button type="submit" className="w-full py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg">{editingAsset?'Update Asset':'Add Asset & Scan'}</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetInventory;
